import { useState, useRef, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Message } from "@/types/chat";
import { useTokenManagement } from "@/hooks/useTokenManagement";

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000;

export function useAIChat() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { checkTokens, recordTokenUsage } = useTokenManagement();
  const [retryCount, setRetryCount] = useState(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  const handleRateLimit = async (
    retryAfter: number,
    userInput: string,
    messages: Message[],
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
    conversationId?: string
  ) => {
    console.log('Handling rate limit:', { retryAfter, retryCount });
    
    if (retryCount >= MAX_RETRIES) {
      console.log('Max retries reached');
      setRetryCount(0);
      toast({
        variant: "destructive",
        title: "For mange forsøk",
        description: "Vennligst vent noen minutter før du prøver igjen.",
      });
      return;
    }

    const delay = retryAfter * 1000 || INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
    console.log(`Setting up retry with delay: ${delay/1000} seconds`);
    
    toast({
      title: "Ventetid påkrevd",
      description: `Systemet er opptatt. Prøver igjen om ${Math.ceil(delay/1000)} sekunder.`,
      duration: delay,
    });
    
    setRetryCount(prev => prev + 1);

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    retryTimeoutRef.current = setTimeout(() => {
      console.log('Executing retry attempt:', retryCount + 1);
      sendMessage(userInput, messages, setMessages, conversationId);
    }, delay);
  };

  const sendMessage = async (
    userInput: string,
    messages: Message[],
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
    conversationId?: string
  ) => {
    if (!userInput.trim() || isLoading) return;

    setIsLoading(true);

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        toast({
          variant: "destructive",
          title: "Autentiseringsfeil",
          description: "Vennligst logg inn på nytt."
        });
        return;
      }

      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: userInput,
        created_at: new Date().toISOString()
      };

      console.log('Making request to chat-completion function');
      
      const { data: response, error } = await supabase.functions.invoke('chat-completion', {
        body: {
          messages: messages.concat(userMessage).map(msg => ({
            role: msg.role,
            content: msg.content
          })),
          conversationId,
          user_id: user.id
        }
      }).catch(error => {
        // Handle network or URL-related errors
        if (error?.message?.includes('404')) {
          throw new Error('Could not connect to AI service. Please check your connection and try again.');
        }
        throw error;
      });

      if (error) {
        console.error('Error from chat-completion:', error);
        
        if (error.message.includes('429') || error.message.toLowerCase().includes('rate limit')) {
          const retryAfter = 30;
          await handleRateLimit(retryAfter, userInput, messages, setMessages, conversationId);
          return;
        }
        throw error;
      }

      console.log('Successfully received response:', response);
      setRetryCount(0);
      
      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: response.content,
        created_at: new Date().toISOString()
      };

      // Insert AI consultation record
      const { error: consultationError } = await supabase
        .from('ai_consultations')
        .insert({
          question: userInput,
          response: response.content,
          user_id: user.id
        });

      if (consultationError) {
        console.error('Error recording AI consultation:', consultationError);
      }

      setMessages(prev => [...prev, userMessage, assistantMessage]);
      await recordTokenUsage('ai-chat', 'ai-chat');

    } catch (error: any) {
      console.error('Error in AI chat:', error);
      toast({
        variant: "destructive",
        title: "Feil ved AI-assistanse",
        description: error.message || "Kunne ikke prosessere forespørselen. Vennligst prøv igjen senere."
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    sendMessage
  };
}
